import {
  ResponseStatus,
  confirmedPaymentRoutes,
  waitingPaymentRoutes,
  RoutesPath,
  ApiPaymentMethods,
} from "src/constants";
interface RouteValidation {
  route: boolean;
  paymentMethod: boolean;
}
interface ConfirmedEventInfo {
  timestamp: string;
  found: boolean;
}

export const getPaymentMethodValidations = (
  pathname: string,
  token: string,
  data: CheckoutType,
): Record<string, RouteValidation> => {
  const paymentMethod =
    data.paymentMethod ?? data.paymentInstruments?.[0]?.method ?? "";

  return {
    bankSlip: {
      route: pathname === `/${token}${RoutesPath.BANK_SLIP}`,
      paymentMethod: paymentMethod === ApiPaymentMethods.BANK_SLIP,
    },
    pix: {
      route: pathname === `/${token}${RoutesPath.PIX}`,
      paymentMethod:
        paymentMethod === ApiPaymentMethods.PIX ||
        paymentMethod === ApiPaymentMethods.PIX_AUTO,
    },
  };
};

export const getConfirmedEventInfo = (
  events: EventsType[],
): ConfirmedEventInfo => {
  const confirmedEvent = events?.find(
    (event) => event.status === ResponseStatus.CONFIRMED,
  );

  const result = {
    timestamp: confirmedEvent?.updatedAt ?? "",
    found: !!confirmedEvent?.updatedAt,
  };

  return result;
};

export const verifyPaymentConfirmed = (
  status: boolean,
  confirmedEventInfo: ConfirmedEventInfo,
) => {
  if (!status || !confirmedEventInfo.found) {
    return false;
  }

  const currentTimestamp = new Date().getTime();
  const confirmedTimestamp = new Date(confirmedEventInfo.timestamp).getTime();
  const timeDifference = currentTimestamp - confirmedTimestamp;
  const TEN_SECONDS = 10000;

  return timeDifference >= TEN_SECONDS;
};

export const NavigationHandlers = {
  handleInvalidPaymentAccess: (
    validations: Record<string, RouteValidation>,
    navigate: (path: string) => void | Promise<void>,
    token: string,
  ) => {
    const hasInvalidAccess = Object.values(validations).some(
      ({ route, paymentMethod }) => route && !paymentMethod,
    );

    if (hasInvalidAccess) {
      void navigate(`/${token}${RoutesPath.PAYMENT_METHODS}`);
      return true;
    }
    return false;
  },
};

export const getStatusCheck = (
  data: CheckoutType,
  pathname: string,
  token: string,
) => {
  const paymentMethod =
    data.paymentMethod ?? data.paymentInstruments?.[0]?.method ?? "";

  const status = data.status;
  const isPaymentConfirmed = status === ResponseStatus.CONFIRMED;
  const confirmedEventInfo = getConfirmedEventInfo(data.events);

  return {
    paymentMethod,
    status,
    isPaymentScheduled: data.isPaymentScheduled,
    isBankSlipPaymentMethod: paymentMethod === ApiPaymentMethods.BANK_SLIP,
    isPixPaymentMethod:
      paymentMethod === ApiPaymentMethods.PIX ||
      paymentMethod === ApiPaymentMethods.PIX_AUTO,
    isBankSlipRoute: pathname === `/${token}${RoutesPath.BANK_SLIP}`,
    isPixRoute: pathname === `/${token}${RoutesPath.PIX}`,
    isPendingStatus: status === ResponseStatus.PENDING,
    isConfirmedStatusAndPaymentMethodRoute:
      status === ResponseStatus.CONFIRMED &&
      paymentMethod in confirmedPaymentRoutes,
    isPreAuthorizationErrorWithoutPaymentMethod:
      status === ResponseStatus.PRE_AUTHORIZATION_ERROR && !paymentMethod,
    isPaymentMethodSelectionRoute:
      pathname === `/${token}${RoutesPath.PAYMENT_METHODS}`,
    isWaitingPaymentRoute: status in waitingPaymentRoutes,
    isPreAuthorizationErrorStatus:
      status === ResponseStatus.PRE_AUTHORIZATION_ERROR,
    isPaymentConfirmedLongTimeAgo: verifyPaymentConfirmed(
      isPaymentConfirmed,
      confirmedEventInfo,
    ),
    isFutureStatus: status === ResponseStatus.FUTURE,
  };
};

export const isAValidRoute = (pathname: string) => {
  const matchingRoute = Object.values(RoutesPath).some((route) =>
    pathname.includes(route),
  );

  return matchingRoute;
};
